import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SurveyContext } from "../context/SurveyContext";
import { AuthContext } from "../context/AuthContext";
import { SocketContext } from "../context/SocketContext";
import api from "../services/api";
import SurveyEngine from "./survey/SurveyEngine";
import { buildStimulusPerceptionPages } from "../data/stimulus_pages";
import { FaSpinner, FaCheck, FaExclamationTriangle, FaArrowRight, FaUsers, FaComments } from "react-icons/fa";

// Population progress indicator — small pill (pre-survey) or full screen (waiting phase)
const PopulationProgress = ({ progress, ready, variant = "minimized" }) => {
  if (ready) {
    if (variant === "minimized") {
      return (
        <div className="flex items-center gap-2">
          <FaCheck className="w-4 h-4 text-spiders-success" />
          <span className="text-sm text-spiders-success font-medium">Your discussion is ready!</span>
        </div>
      );
    }
    return null; // Full variant auto-transitions
  }

  // Error state
  if (progress?.error) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-spiders-error font-medium">
          ⚠ Setup failed — please refresh and try again
        </span>
      </div>
    );
  }

  if (!progress) {
    if (variant === "minimized") return null;
    return (
      <div className="flex items-center gap-2">
        <FaSpinner className="w-4 h-4 text-spiders-primary-400 animate-spin" />
        <span className="text-spiders-text-muted text-sm">Initializing...</span>
      </div>
    );
  }

  const agents = progress.agentsCreated || 0;
  const agentsTotal = progress.agentsTotal || 12;
  const posts = progress.postsCreated || 0;
  const postsTotal = progress.postsTotal || 24;
  const comments = progress.commentsCreated || 0;
  const commentsTotal = progress.commentsTotal || 36;

  // Weighted progress: agents 20%, posts 50%, comments 30%
  const pct = Math.round(
    (agents / Math.max(agentsTotal, 1)) * 20 +
    (posts / Math.max(postsTotal, 1)) * 50 +
    (comments / Math.max(commentsTotal, 1)) * 30
  );

  // Minimized: compact card for bottom-left corner during pre-survey
  if (variant === "minimized") {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <FaSpinner className="w-4 h-4 text-spiders-primary-400 animate-spin flex-shrink-0" />
          <span className="text-sm text-spiders-text-primary font-medium">Preparing your discussion</span>
        </div>
        <div className="w-full bg-spiders-bg-elevated rounded-full h-2 overflow-hidden">
          <div
            className="h-2 rounded-full transition-all duration-500 bg-gradient-to-r from-spiders-primary-500 to-spiders-accent-400"
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <div className="flex justify-between">
          <div className="flex items-center gap-1.5">
            <FaUsers className="w-3 h-3 text-spiders-primary-400" />
            <span className="text-xs text-spiders-text-secondary">{agents}/{agentsTotal}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FaComments className="w-3 h-3 text-spiders-accent-400" />
            <span className="text-xs text-spiders-text-secondary">{posts}/{postsTotal}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FaComments className="w-3 h-3 text-spiders-success" />
            <span className="text-xs text-spiders-text-secondary">{comments}/{commentsTotal}</span>
          </div>
        </div>
      </div>
    );
  }

  // Full variant — used in waiting_for_instance phase
  return (
    <div className="text-center max-w-md mx-4">
      <FaSpinner className="w-8 h-8 text-spiders-primary-400 animate-spin mx-auto mb-4" />
      <h2 className="text-lg font-bold text-spiders-text-primary mb-2">Almost ready!</h2>
      <p className="text-spiders-text-secondary text-sm mb-6">
        Finalizing your discussion environment...
      </p>
      <div className="w-full bg-spiders-bg-elevated rounded-full h-3 mb-3 overflow-hidden">
        <div
          className="h-3 rounded-full transition-all duration-500 bg-gradient-to-r from-spiders-primary-500 to-spiders-accent-400"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-spiders-bg-surface border border-spiders-border rounded-lg p-3">
          <FaUsers className="text-spiders-primary-400 mx-auto mb-1" />
          <p className="text-spiders-text-primary font-semibold text-sm">{agents}/{agentsTotal}</p>
          <p className="text-spiders-text-muted text-xs">Members</p>
        </div>
        <div className="bg-spiders-bg-surface border border-spiders-border rounded-lg p-3">
          <FaComments className="text-spiders-accent-400 mx-auto mb-1" />
          <p className="text-spiders-text-primary font-semibold text-sm">{posts}/{postsTotal}</p>
          <p className="text-spiders-text-muted text-xs">Posts</p>
        </div>
        <div className="bg-spiders-bg-surface border border-spiders-border rounded-lg p-3">
          <FaComments className="text-spiders-success mx-auto mb-1" />
          <p className="text-spiders-text-primary font-semibold text-sm">{comments}/{commentsTotal}</p>
          <p className="text-spiders-text-muted text-xs">Replies</p>
        </div>
      </div>
      <p className="text-spiders-text-muted text-xs">{Math.min(pct, 100)}% complete</p>
    </div>
  );
};

// Simple markdown-to-HTML renderer for trusted content (study config, not user input)
const renderMarkdown = (text) => {
  if (!text) return "";

  // Split into blocks by double newlines
  const blocks = text.split(/\n\n+/);
  const html = blocks.map((block) => {
    const trimmed = block.trim();
    if (!trimmed) return "";

    // Headers
    if (trimmed.startsWith("### ")) return `<h3>${inline(trimmed.slice(4))}</h3>`;
    if (trimmed.startsWith("## ")) return `<h2>${inline(trimmed.slice(3))}</h2>`;
    if (trimmed.startsWith("# ")) return `<h1>${inline(trimmed.slice(2))}</h1>`;

    // Unordered list (block of lines starting with "- ")
    if (/^- /m.test(trimmed) && trimmed.split("\n").every((l) => l.startsWith("- ") || l === "")) {
      const items = trimmed.split("\n").filter((l) => l.startsWith("- "))
        .map((l) => `<li>${inline(l.slice(2))}</li>`).join("");
      return `<ul>${items}</ul>`;
    }

    // Ordered list (block of lines starting with "1. ", "2. ", etc.)
    if (/^\d+\. /m.test(trimmed) && trimmed.split("\n").every((l) => /^\d+\. /.test(l) || l === "")) {
      const items = trimmed.split("\n").filter((l) => /^\d+\. /.test(l))
        .map((l) => `<li>${inline(l.replace(/^\d+\. /, ""))}</li>`).join("");
      return `<ol>${items}</ol>`;
    }

    // Regular paragraph — convert single newlines to <br/>
    return `<p>${inline(trimmed).replace(/\n/g, "<br/>")}</p>`;
  }).join("");

  return html;
};

// Inline formatting (bold, italic)
const inline = (text) =>
  text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");

// Consent page component
const ConsentPage = ({ consent, onAccept }) => {
  const [accepted, setAccepted] = useState(false);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-spiders-text-primary text-center mb-6">
        {consent?.title || "Study Information & Consent"}
      </h1>
      <div className="spiders-card p-6 mb-6">
        <div
          className="prose prose-sm prose-invert max-w-none text-spiders-text-secondary leading-relaxed
            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-spiders-text-primary [&_h2]:mt-5 [&_h2]:mb-2
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-spiders-text-primary [&_h3]:mt-4 [&_h3]:mb-2
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5 [&_ul]:space-y-0.5
            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5 [&_ol]:space-y-0.5
            [&_li]:text-spiders-text-secondary [&_li]:leading-snug
            [&_p]:mb-2
            [&_strong]:text-spiders-text-primary"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(consent?.content) || "No consent information provided." }}
        />
      </div>
      {consent?.requireAcknowledgment !== false && (
        <label className="flex items-start gap-3 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded border-spiders-border accent-spiders-primary-500"
          />
          <span className="text-sm text-spiders-text-secondary">
            I have read and understood the study information above and I consent to participate.
          </span>
        </label>
      )}
      <button
        onClick={onAccept}
        disabled={consent?.requireAcknowledgment !== false && !accepted}
        className={`w-full py-3 rounded-lg font-semibold transition-all ${
          consent?.requireAcknowledgment === false || accepted
            ? "spiders-btn-primary"
            : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
        }`}
      >
        I Agree to Participate
      </button>
    </div>
  );
};

// Debriefing page component
const DebriefingPage = ({ debriefing, prolificConfig, onComplete }) => {
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-spiders-text-primary text-center mb-6">
        {debriefing?.title || "Study Complete"}
      </h1>
      <div className="spiders-card p-6 mb-6">
        <div
          className="prose prose-sm prose-invert max-w-none text-spiders-text-secondary leading-relaxed
            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-spiders-text-primary [&_h2]:mt-5 [&_h2]:mb-2
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1
            [&_p]:mb-3
            [&_strong]:text-spiders-text-primary"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(debriefing?.content) || "Thank you for your participation in this study." }}
        />
      </div>
      <div className="text-center">
        {prolificConfig?.redirectUrl ? (
          <div className="mb-5">
            <p className="text-lg font-bold text-spiders-text-primary mb-1">
              Almost done — one last step
            </p>
            <p className="text-sm text-spiders-text-secondary">
              Click the button below to validate your participation on{" "}
              {prolificConfig?.panelName || "the panel"}.{" "}
              <strong className="text-spiders-text-primary">
                You must click it to receive your compensation.
              </strong>
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-green-400 mb-4">
            <FaCheck className="w-5 h-5" />
            <span className="font-semibold">Your responses have been recorded</span>
          </div>
        )}
        {prolificConfig?.completionCode && (
          <div className="spiders-card p-4 mb-6">
            <p className="text-sm text-spiders-text-muted mb-1">Your completion code:</p>
            <p className="text-2xl font-bold text-spiders-primary-400 font-mono">
              {prolificConfig.completionCode}
            </p>
          </div>
        )}
        <button
          onClick={onComplete}
          className="spiders-btn-primary py-3 px-8"
        >
          {prolificConfig?.redirectUrl
            ? `Validate my participation on ${prolificConfig?.panelName || "the panel"}`
            : "Finish"}
        </button>
      </div>
    </div>
  );
};

const StudyFlow = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const studyId = searchParams.get("s");
  const participantId = searchParams.get("tid") || searchParams.get("pid");
  const isTestMode = searchParams.get("test") === "1";

  const {
    updateInstance,
    updateCondition,
    updateParticipantInfo,
    resetOnboarding,
    completeOnboarding,
    setInitialized,
  } = useContext(SurveyContext);
  const { user, setAuthFromToken, logout } = useContext(AuthContext);
  const { authenticateSocket } = useContext(SocketContext);

  // Study flow state
  const [phase, setPhase] = useState("loading");
  const [studyConfig, setStudyConfig] = useState(null);
  const [assignmentId, setAssignmentId] = useState(null);
  const [assignedConditionId, setAssignedConditionId] = useState(null);
  const [conditionFactors, setConditionFactors] = useState(null); // { communicationStyle, groupFocus }
  const [error, setError] = useState(null);

  // Parallel population state
  const [bgInstanceId, setBgInstanceId] = useState(null);
  const [bgInstanceReady, setBgInstanceReady] = useState(false);
  const [bgPopulationProgress, setBgPopulationProgress] = useState(null);
  const bgPollingRef = useRef(null);

  // Extract the topic article from preSurvey for platform display
  const extractTopicArticle = (config) => {
    const introPage = config?.preSurvey?.pages?.find((p) => p.pageId === "topic_intro");
    const articleQ = introPage?.questions?.find((q) => q.type === "news_article");
    return articleQ?.article || null;
  };

  // Persist study session
  const STORAGE_KEY = `spiders_study_${studyId}_${participantId}`;

  const saveStudySession = useCallback(
    (data) => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error("Error saving study session:", e);
      }
    },
    [STORAGE_KEY]
  );

  const loadStudySession = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, [STORAGE_KEY]);

  // Initialize study
  useEffect(() => {
    if (!studyId || !participantId) {
      setError("Missing required parameters. Please use the link provided by the research team.");
      setPhase("error");
      return;
    }

    initializeStudy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId, participantId]);

  const initializeStudy = async () => {
    setPhase("loading");
    setError(null);

    try {
      // Fetch study config (retry once on failure — may hit rate limit after redirect)
      let config;
      try {
        const resp = await api.get(`/studies/${studyId}/config`);
        config = resp.data;
      } catch (configErr) {
        console.warn("Study config fetch failed, retrying in 2s:", configErr.response?.status);
        await new Promise((r) => setTimeout(r, 2000));
        const resp = await api.get(`/studies/${studyId}/config`);
        config = resp.data;
      }
      setStudyConfig(config);

      // Always check server for assignment state (localStorage may be stale after test cleanup)
      const { data: serverStatus } = await api.get(
        `/studies/${studyId}/assignment/${participantId}`
      );

      // Merge with localStorage for client-only flags (platformDone, registered)
      const session = loadStudySession() || {};

      if (!serverStatus.exists) {
        // No server-side assignment — clear ALL stale session data, start fresh
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(`spiders_survey_pre_${studyId}_${participantId}`);
        sessionStorage.removeItem(`spiders_survey_post_${studyId}_${participantId}`);
        sessionStorage.removeItem("spiders_session");
        sessionStorage.removeItem("spiders_study_return_url");
        // Clear stale auth token from previous test runs — ensures user must re-register
        if (user) {
          await logout();
        }
        setPhase("consent");
      } else {
        setAssignmentId(serverStatus.assignmentId);
        setAssignedConditionId(serverStatus.conditionId);
        setConditionFactors(serverStatus.conditionFactors || session.conditionFactors || null);

        // Sync localStorage with server state
        saveStudySession({
          ...session,
          assignmentId: serverStatus.assignmentId,
          conditionId: serverStatus.conditionId,
          conditionFactors: serverStatus.conditionFactors || session.conditionFactors || null,
          instanceId: serverStatus.instanceId,
          preSurveyCompleted: serverStatus.preSurveyCompleted,
          postSurveyCompleted: serverStatus.postSurveyCompleted,
        });

        if (serverStatus.completedAt || session.completed) {
          setPhase("complete");
        } else if (serverStatus.postSurveyCompleted) {
          setPhase("debriefing");
        } else if (serverStatus.platformDone || session.platformDone) {
          setPhase("post_survey_transition");
        } else if (!serverStatus.preSurveyCompleted && serverStatus.instanceId) {
          // Assignment exists + instance created, but pre-survey not done yet.
          // This happens on page refresh during pre-survey (background creation already ran).
          // Resume pre-survey with instance polling in background.
          setBgInstanceId(serverStatus.instanceId);
          updateInstance({ _id: serverStatus.instanceId, conditionId: serverStatus.conditionId });
          setPhase("pre_survey");
        } else if (serverStatus.instanceId && serverStatus.registered) {
          // User registered — go to platform (reauth + navigate)
          setupPlatformAndNavigate(serverStatus.conditionId, serverStatus.instanceId, config);
          return;
        } else if (serverStatus.instanceId) {
          // Instance exists + pre-survey done + user not registered — go to platform
          setupPlatformAndNavigate(serverStatus.conditionId, serverStatus.instanceId, config);
          return;
        } else if (serverStatus.preSurveyCompleted) {
          setPhase("assigning");
        } else {
          setPhase("consent");
        }
      }
    } catch (err) {
      console.error("Error initializing study:", err);
      console.error("Error details:", err.response?.status, err.response?.data);
      // Don't show error for rate limiting — just retry after a moment
      if (err.response?.status === 429) {
        console.warn("Rate limited during study init, retrying in 3s...");
        setTimeout(initializeStudy, 3000);
        return;
      }
      setError(err.response?.data?.message || "Failed to load study. Please try again.");
      setPhase("error");
    }
  };

  // Setup SurveyContext for platform phase and navigate
  // Navigates to /newsfeed so InstanceLoader handles progress bar → then Register → then onboarding
  const setupPlatformAndNavigate = async (condId, instId, config) => {
    try {
      updateParticipantInfo(participantId, null);
      updateCondition(condId);

      const { data: inst } = await api.get(`/instances/${instId}`);
      updateInstance(inst);
      // For returning users: mark onboarding as completed since they already did it
      if (inst.participantUserId || inst.status === "active") {
        completeOnboarding();
      }
      setInitialized(true);

      // Ensure participant is authenticated (for resume path)
      // On fresh flow, user hasn't registered yet — PrivateRoute will redirect to /register
      // On resume, the token may have expired so we reauth
      if (!user) {
        try {
          const { data: authData } = await api.post(`/studies/${studyId}/reauth`, {
            participantId,
            instanceId: instId,
          });
          if (authData.token) {
            await setAuthFromToken(authData.token, {
              userId: authData.userId,
              username: authData.username,
              instanceId: authData.instanceId,
              condition: authData.condition,
            });
            if (authenticateSocket) {
              authenticateSocket(authData.token);
            }
          }
        } catch (authErr) {
          console.error("Error re-authenticating participant:", authErr);
          // If reauth fails, the user may need to re-register
        }
      }

      // Set study mode globals so Navbar/MobileTopBar know to trigger post-survey
      window.__studyMode = true;
      window.__studyTestMode = isTestMode;
      window.__studySessionConfig = config?.sessionConfig;
      window.__studyTopicArticle = extractTopicArticle(config);
      const testParam = isTestMode ? "&test=1" : "";
      window.__studyReturnUrl = `/study?s=${studyId}&pid=${participantId}${testParam}`;
      sessionStorage.setItem("spiders_study_return_url", window.__studyReturnUrl);
      window.__studyPlatformDone = async () => {
        const session = loadStudySession() || {};
        // Verify minimum time with server before allowing transition
        if (session.assignmentId) {
          try {
            const apiUrl = process.env.REACT_APP_API_URL || "/api";
            const resp = await fetch(`${apiUrl}/studies/${studyId}/assignment/${session.assignmentId}/platform-done`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
            });
            if (!resp.ok) {
              const data = await resp.json().catch(() => ({}));
              if (resp.status === 403 && data.remainingMinutes) {
                alert(data.message);
                return; // Block navigation — minimum time not reached
              }
            }
          } catch (e) {
            // Network error — allow through (don't trap participant)
          }
        }
        saveStudySession({ ...session, platformDone: true });
        window.location.href = `/study?s=${studyId}&pid=${participantId}${testParam}`;
      };

      // Navigate to main app (InstanceLoader will handle loading/onboarding/newsfeed)
      navigate("/newsfeed");
    } catch (e) {
      console.error("Error setting up platform:", e);
      setError("Failed to set up the platform. Please try again.");
      setPhase("error");
    }
  };

  // Handle consent acceptance — start instance creation in background
  const handleConsent = () => {
    setPhase("pre_survey");

    // Fire-and-forget: assign condition + create instance while participant answers pre-survey
    (async () => {
      try {
        // Step 1: Assign condition
        const { data: assignment } = await api.post(`/studies/${studyId}/assign`, {
          participantId,
          isTest: isTestMode,
        });

        setAssignmentId(assignment.assignmentId);
        setAssignedConditionId(assignment.conditionId);
        if (assignment.conditionFactors) setConditionFactors(assignment.conditionFactors);

        // Write session early so api.js interceptor has conditionId
        sessionStorage.removeItem("condition");
        sessionStorage.setItem(
          "spiders_session",
          JSON.stringify({ conditionId: assignment.conditionId, participantId })
        );
        saveStudySession({
          assignmentId: assignment.assignmentId,
          conditionId: assignment.conditionId,
          conditionFactors: assignment.conditionFactors || null,
        });

        updateParticipantInfo(participantId, null);
        updateCondition(assignment.conditionId);

        // Step 2: Create instance (triggers orchestrator population)
        const { data: instanceData } = await api.post("/instances", {
          conditionId: assignment.conditionId,
          participantId,
          isTest: isTestMode,
        });

        const instId = instanceData.instance._id;
        setBgInstanceId(instId);

        // Link instance to assignment
        await api.patch(
          `/studies/${studyId}/assignment/${assignment.assignmentId}/instance`,
          { instanceId: instId }
        );

        saveStudySession({
          assignmentId: assignment.assignmentId,
          conditionId: assignment.conditionId,
          conditionFactors: assignment.conditionFactors || null,
          instanceId: instId,
        });

        updateInstance(instanceData.instance);

        // Step 3: Start polling for population completion
        bgPollingRef.current = setInterval(async () => {
          try {
            const { data: status } = await api.get(`/instances/${instId}/status`);
            setBgPopulationProgress(status.progress || null);
            if (status.isReady || status.status === "ready") {
              setBgInstanceReady(true);
              clearInterval(bgPollingRef.current);
              bgPollingRef.current = null;
            } else if (status.status === "error") {
              console.error("Population failed:", status.statusMessage);
              clearInterval(bgPollingRef.current);
              bgPollingRef.current = null;
              setBgPopulationProgress({ error: true, message: status.statusMessage || "Population failed. Please try again." });
            }
          } catch (e) {
            // Polling error — continue silently
          }
        }, 3000);
      } catch (err) {
        // Background creation failed — will be retried in handlePreSurveyComplete
        console.warn("Background instance creation failed, will retry after pre-survey:", err.message);
      }
    })();
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (bgPollingRef.current) {
        clearInterval(bgPollingRef.current);
      }
    };
  }, []);

  // Auto-transition: when instance becomes ready while on waiting screen, proceed to platform
  useEffect(() => {
    if (phase === "waiting_for_instance" && bgInstanceReady && bgInstanceId && assignedConditionId) {
      setPhase("platform_intro");
    }
    // Also restart polling if we're on waiting screen but polling stopped
    if (phase === "waiting_for_instance" && !bgInstanceReady && bgInstanceId && !bgPollingRef.current) {
      bgPollingRef.current = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/instances/${bgInstanceId}/status`);
          setBgPopulationProgress(status.progress || null);
          if (status.isReady || status.status === "ready") {
            setBgInstanceReady(true);
            clearInterval(bgPollingRef.current);
            bgPollingRef.current = null;
          } else if (status.status === "error") {
            console.error("Population failed:", status.statusMessage);
            clearInterval(bgPollingRef.current);
            bgPollingRef.current = null;
            setBgPopulationProgress({ error: true, message: status.statusMessage || "Population failed. Please try again." });
          }
        } catch (e) {
          // Continue silently
        }
      }, 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, bgInstanceReady, bgInstanceId, assignedConditionId]);

  // Navigate to platform (shared helper for post-survey-complete and waiting_for_instance)
  const proceedToPlatform = async (instId, condId) => {
    resetOnboarding();

    // Ensure no stale auth from previous test runs
    if (user) {
      await logout();
    }

    setInitialized(true);

    // Set study mode globals before navigating away from /study
    window.__studyMode = true;
    window.__studyTestMode = isTestMode;
    window.__studySessionConfig = studyConfig?.sessionConfig;
    window.__studyTopicArticle = extractTopicArticle(studyConfig);
    const testParam = isTestMode ? "&test=1" : "";
    window.__studyReturnUrl = `/study?s=${studyId}&pid=${participantId}${testParam}`;
    sessionStorage.setItem("spiders_study_return_url", window.__studyReturnUrl);
    window.__studyPlatformDone = async () => {
      const session = loadStudySession() || {};
      if (session.assignmentId) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || "/api";
          const resp = await fetch(`${apiUrl}/studies/${studyId}/assignment/${session.assignmentId}/platform-done`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
          });
          if (!resp.ok) {
            const data = await resp.json().catch(() => ({}));
            if (resp.status === 403 && data.remainingMinutes) {
              alert(data.message);
              return;
            }
          }
        } catch (e) {
          // Network error — allow through
        }
      }
      saveStudySession({ ...session, platformDone: true });
      window.location.href = `/study?s=${studyId}&pid=${participantId}${testParam}`;
    };

    navigate("/newsfeed");
  };

  // Handle pre-survey completion
  const handlePreSurveyComplete = async (responses, startedAt, durationMs) => {
    // Stop background polling if still running
    if (bgPollingRef.current) {
      clearInterval(bgPollingRef.current);
      bgPollingRef.current = null;
    }

    setPhase("assigning");

    try {
      // If background creation didn't happen (e.g., failed silently), do it now
      let currentAssignmentId = assignmentId;
      let currentConditionId = assignedConditionId;
      let currentInstanceId = bgInstanceId;

      if (!currentAssignmentId) {
        // Background assignment failed — do it now
        const { data: assignment } = await api.post(`/studies/${studyId}/assign`, {
          participantId,
          isTest: isTestMode,
        });
        currentAssignmentId = assignment.assignmentId;
        currentConditionId = assignment.conditionId;
        setAssignmentId(assignment.assignmentId);
        setAssignedConditionId(assignment.conditionId);
        if (assignment.conditionFactors) setConditionFactors(assignment.conditionFactors);

        sessionStorage.removeItem("condition");
        sessionStorage.setItem(
          "spiders_session",
          JSON.stringify({ conditionId: assignment.conditionId, participantId })
        );
        updateParticipantInfo(participantId, null);
        updateCondition(assignment.conditionId);
      }

      if (!currentInstanceId) {
        // Background instance creation failed — do it now
        const { data: instanceData } = await api.post("/instances", {
          conditionId: currentConditionId,
          participantId,
          isTest: isTestMode,
        });
        currentInstanceId = instanceData.instance._id;
        setBgInstanceId(currentInstanceId);
        updateInstance(instanceData.instance);

        await api.patch(
          `/studies/${studyId}/assignment/${currentAssignmentId}/instance`,
          { instanceId: currentInstanceId }
        );
      }

      // Submit pre-survey responses
      await api.post(`/studies/${studyId}/survey-response`, {
        assignmentId: currentAssignmentId,
        surveyType: "pre",
        responses,
        startedAt: new Date(startedAt).toISOString(),
        durationMs,
      });

      // Save session
      saveStudySession({
        assignmentId: currentAssignmentId,
        conditionId: currentConditionId,
        conditionFactors: conditionFactors,
        instanceId: currentInstanceId,
        preSurveyCompleted: true,
      });

      // If instance is already ready (populated during pre-survey), go to the
      // platform_intro bridge page. Otherwise show the waiting screen and let
      // the auto-transition useEffect advance to platform_intro once ready.
      if (bgInstanceReady) {
        setPhase("platform_intro");
      } else {
        setPhase("waiting_for_instance");
      }
    } catch (err) {
      console.error("Error completing pre-survey:", err);
      setError("Failed to process your survey responses. Please try again.");
      setPhase("error");
    }
  };

  // Handle post-survey completion
  const handlePostSurveyComplete = async (responses, startedAt, durationMs) => {
    try {
      await api.post(`/studies/${studyId}/survey-response`, {
        assignmentId,
        surveyType: "post",
        responses,
        startedAt: new Date(startedAt).toISOString(),
        durationMs,
      });

      const session = loadStudySession() || {};
      saveStudySession({ ...session, postSurveyCompleted: true });

      setPhase("debriefing");
    } catch (err) {
      console.error("Error submitting post-survey:", err);
      setError("Failed to submit your responses. Please try again.");
      setPhase("error");
    }
  };

  // Handle study completion
  const handleStudyComplete = async () => {
    try {
      await api.patch(`/studies/${studyId}/assignment/${assignmentId}/complete`);

      const session = loadStudySession() || {};
      saveStudySession({ ...session, completed: true });
      sessionStorage.removeItem("spiders_study_return_url");

      // Redirect to Prolific
      if (studyConfig?.prolificConfig?.redirectUrl) {
        const url = studyConfig.prolificConfig.redirectUrl +
          (studyConfig.prolificConfig.completionCode || "");
        window.location.href = url;
      } else {
        setPhase("complete");
      }
    } catch (err) {
      console.error("Error completing study:", err);
      // Still redirect even if API call fails
      if (studyConfig?.prolificConfig?.redirectUrl) {
        window.location.href =
          studyConfig.prolificConfig.redirectUrl +
          (studyConfig.prolificConfig.completionCode || "");
      }
    }
  };

  // Test mode banner (floating, shown on all phases)
  const TestBanner = () =>
    isTestMode ? (
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-600 text-white text-center text-xs py-1 font-medium">
        TEST MODE — This data will be marked as test and excluded from exports
      </div>
    ) : null;

  // Error state
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="spiders-card p-8 max-w-md mx-4 text-center">
          <FaExclamationTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-spiders-text-primary mb-2">Something went wrong</h2>
          <p className="text-spiders-text-secondary mb-6">{error}</p>
          <button onClick={initializeStudy} className="spiders-btn-primary py-2 px-6">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-8 h-8 text-spiders-primary-400 animate-spin mx-auto mb-4" />
          <p className="text-spiders-text-secondary">Loading study...</p>
        </div>
      </div>
    );
  }

  // Consent — interpolate {{panel}} placeholder with configured panel name
  if (phase === "consent") {
    const panelName = studyConfig?.prolificConfig?.panelName || "Panel";
    const consentWithPanel = studyConfig?.consent?.content
      ? {
          ...studyConfig.consent,
          content: studyConfig.consent.content.replace(/\{\{panel\}\}/g, panelName),
        }
      : studyConfig?.consent;
    return (
      <div className="min-h-screen bg-spiders-bg">
        <TestBanner />
        <ConsentPage consent={consentWithPanel} onAccept={handleConsent} />
      </div>
    );
  }

  // Pre-survey — with population progress pill in bottom-left
  if (phase === "pre_survey") {
    // Only show progress pill if instance is still populating (not when already ready from template)
    const showProgress = bgInstanceId && bgPopulationProgress && !bgInstanceReady;
    return (
      <div className="min-h-screen bg-spiders-bg">
        <TestBanner />
        <SurveyEngine
          surveyConfig={studyConfig?.preSurvey}
          onComplete={handlePreSurveyComplete}
          title="Pre-Study Questionnaire"
          storageKey={studyId && participantId ? `spiders_survey_pre_${studyId}_${participantId}` : null}
          draftEndpoint={assignmentId ? `/studies/${studyId}/survey-draft` : null}
          draftMeta={{ assignmentId, surveyType: "pre" }}
        />
        {/* Progress card — bottom-left, never obscures navigation */}
        {showProgress && (
          <div className="fixed bottom-4 left-4 z-40 px-5 py-4 bg-spiders-bg-surface/95 backdrop-blur-sm border border-spiders-border rounded-xl shadow-lg w-72">
            <PopulationProgress progress={bgPopulationProgress} ready={bgInstanceReady} variant="minimized" />
          </div>
        )}
      </div>
    );
  }

  // Assigning (brief transition while creating instance)
  if (phase === "assigning") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <TestBanner />
        <div className="text-center max-w-sm mx-4">
          <FaSpinner className="w-8 h-8 text-spiders-primary-400 animate-spin mx-auto mb-4" />
          <p className="text-spiders-text-secondary font-medium">Processing your responses...</p>
        </div>
      </div>
    );
  }

  // Waiting for instance population — shown only if pre-survey finishes before population
  if (phase === "waiting_for_instance") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <TestBanner />
        <PopulationProgress progress={bgPopulationProgress} ready={bgInstanceReady} variant="full" />
      </div>
    );
  }

  // Pre-platform narrative bridge: shown after pre-survey + instance ready,
  // before the participant is sent through to /register and the platform.
  // Threads the cover story into the platform experience. Uses the same
  // accent-coloured "narrative page" treatment as the SurveyEngine
  // narrative pages so the participant sees a consistent scene/story voice.
  if (phase === "platform_intro") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <TestBanner />
        <div className="max-w-2xl w-full mx-auto px-4 py-6 animate-fadeIn">
          <div className="border-l-4 border-spiders-accent-500 pl-5 sm:pl-6 bg-spiders-accent-500/10 py-6 rounded-r-lg mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-spiders-accent-300 mb-5 leading-tight tracking-tight">
              Up next: a discussion.
            </h1>
            <div className="space-y-4 text-spiders-text-secondary text-base leading-relaxed">
              <p>
                You've seen the announcement, and how the press is reporting it. Next, you'll see a <strong className="text-spiders-text-primary font-semibold">small social platform</strong> carrying a discussion prepared for this study. The voices there are fictional, but the arguments reflect positions people actually take on this topic, and your reactions to them are what we're interested in.
              </p>
              <p>
                <strong className="text-spiders-text-primary font-semibold">Plan to spend about 10 minutes there.</strong> Read what people are saying. React. Comment. Share your own thoughts. <strong className="text-spiders-text-primary font-semibold">Treat it the way you would treat any social platform you use.</strong>
              </p>
            </div>
            <p className="mt-5 text-base text-spiders-text-secondary italic leading-relaxed">
              A short sign-up step comes first.
            </p>
          </div>
          <button
            onClick={() => proceedToPlatform(bgInstanceId, assignedConditionId)}
            disabled={!bgInstanceId || !assignedConditionId}
            className="w-full spiders-btn-accent py-3 text-base flex items-center justify-center gap-2"
          >
            Continue
            <FaArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Transition screen: brief pause between platform and post-survey
  if (phase === "post_survey_transition") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <TestBanner />
        <div className="max-w-md mx-auto px-6 text-center animate-fadeIn">
          <div className="w-16 h-16 rounded-full bg-spiders-primary-500/20 flex items-center justify-center mx-auto mb-6">
            <FaCheck className="w-7 h-7 text-spiders-primary-400" />
          </div>
          <h2 className="text-xl font-bold text-spiders-text-primary mb-3">
            Thank you for participating in the discussion!
          </h2>
          <p className="text-spiders-text-secondary text-sm leading-relaxed mb-8">
            We will now ask you some questions about your experience.
            This final questionnaire takes about 5 minutes.
          </p>
          <button
            onClick={() => setPhase("post_survey")}
            className="spiders-btn-primary px-8 py-3 text-base flex items-center justify-center gap-2 mx-auto"
          >
            Continue to Questionnaire
            <FaArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Post-survey — inject condition-specific stimulus perception pages
  // and interpolate {{topic}} placeholders in page instructions
  if (phase === "post_survey") {
    let postSurveyConfig = studyConfig?.postSurvey;
    // Inject condition-specific stimulus perception pages (if enabled)
    const stimulusEnabled = studyConfig?.sessionConfig?.stimulusPerceptionEnabled;
    if (conditionFactors && postSurveyConfig && stimulusEnabled) {
      const stimulusPages = buildStimulusPerceptionPages(
        conditionFactors.communicationStyle,
        conditionFactors.groupFocus,
        conditionFactors.topicName
      );
      if (stimulusPages.length > 0) {
        const existingPages = postSurveyConfig.pages || [];
        // Insert after manipulation_checks, or at the end if not found
        const mcIndex = existingPages.findIndex((p) => p.pageId === "manipulation_checks");
        const insertAt = mcIndex >= 0 ? mcIndex + 1 : existingPages.length;
        const newPages = [...existingPages];
        newPages.splice(insertAt, 0, ...stimulusPages);
        postSurveyConfig = { ...postSurveyConfig, pages: newPages };
      }
    }

    // Interpolate {{topic}} in page instructions (always, independent of stimulus toggle)
    const topicName = conditionFactors?.topicName;
    if (topicName && postSurveyConfig?.pages) {
      postSurveyConfig = {
        ...postSurveyConfig,
        pages: postSurveyConfig.pages.map((page) =>
          page.instruction && page.instruction.includes("{{topic}}")
            ? { ...page, instruction: page.instruction.replace(/\{\{topic\}\}/g, topicName) }
            : page
        ),
      };
    }
    return (
      <div className="min-h-screen bg-spiders-bg">
        <TestBanner />
        <SurveyEngine
          surveyConfig={postSurveyConfig}
          onComplete={handlePostSurveyComplete}
          title="Post-Study Questionnaire"
          storageKey={studyId && participantId ? `spiders_survey_post_${studyId}_${participantId}` : null}
          draftEndpoint={assignmentId ? `/studies/${studyId}/survey-draft` : null}
          draftMeta={{ assignmentId, surveyType: "post" }}
        />
      </div>
    );
  }

  // Debriefing — also interpolate {{panel}}
  if (phase === "debriefing") {
    const panelName = studyConfig?.prolificConfig?.panelName || "Panel";
    const debriefingWithPanel = studyConfig?.debriefing?.content
      ? {
          ...studyConfig.debriefing,
          content: studyConfig.debriefing.content.replace(/\{\{panel\}\}/g, panelName),
        }
      : studyConfig?.debriefing;
    return (
      <div className="min-h-screen bg-spiders-bg">
        <TestBanner />
        <DebriefingPage
          debriefing={debriefingWithPanel}
          prolificConfig={studyConfig?.prolificConfig}
          onComplete={handleStudyComplete}
        />
      </div>
    );
  }

  // Complete
  if (phase === "complete") {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="spiders-card p-8 max-w-md mx-4 text-center">
          <FaCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-spiders-text-primary mb-2">Study Complete</h2>
          <p className="text-spiders-text-secondary">
            Thank you for your participation. You may close this window.
          </p>
          {studyConfig?.prolificConfig?.completionCode && (
            <div className="mt-4 spiders-card p-3">
              <p className="text-xs text-spiders-text-muted mb-1">Completion code:</p>
              <p className="text-lg font-bold text-spiders-primary-400 font-mono">
                {studyConfig.prolificConfig.completionCode}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default StudyFlow;
